import React, { useEffect, useRef, useState } from 'react';
import {
    createNBackSequence,
    BrainTrainingGameIcon,
    GameButton,
    GameResultOverlay,
    GameShell,
    usePausableTimeout,
} from '../shared';
import { formatGameCopy } from './gameCopy';

function SymbolNBackGame({ copy = {}, initialLevel = 2, isPaused = false, onRunComplete = null, ...props }) {
    const n = Math.max(1, Math.min(3, Math.round(Number(initialLevel) || 2)));
    const completionReportedRef = useRef(false);
    const { clear: clearTimer, schedule: scheduleTimer } = usePausableTimeout(isPaused);
    const [sequence, setSequence] = useState(() => createNBackSequence(24, n));
    const [index, setIndex] = useState(0);
    const [choice, setChoice] = useState(null);
    const [lastCorrect, setLastCorrect] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const done = index >= sequence.length;
    const current = sequence[index] || '';

    const reset = () => {
        completionReportedRef.current = false;
        clearTimer();
        setSequence(createNBackSequence(24, n));
        setIndex(0);
        setChoice(null);
        setLastCorrect(null);
        setScore(0);
        setStreak(0);
    };

    const answer = (isMatchAnswer) => {
        if (isPaused || done || choice !== null) {
            return;
        }

        const isMatch = index >= n && sequence[index] === sequence[index - n];
        const correct = isMatchAnswer === isMatch;
        setChoice(isMatchAnswer);
        setLastCorrect(correct);
        setScore((value) => value + (correct ? 1 : 0));
        setStreak((value) => (correct ? value + 1 : 0));
        scheduleTimer(() => {
            setIndex((value) => value + 1);
            setChoice(null);
            setLastCorrect(null);
        }, correct ? 600 : 850);
    };

    const metrics = { level: n, maxScore: sequence.length, score, streak };

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.(metrics);
    }, [done, metrics.level, metrics.maxScore, metrics.score, metrics.streak, onRunComplete]);

    return (
        <GameShell {...props} copy={copy} disabled={isPaused} metrics={metrics} tone={lastCorrect === true || done ? 'good' : lastCorrect === false ? 'miss' : 'ready'}>
            <div className="UFOV-brain-games__nback-card">
                {done ? <BrainTrainingGameIcon icon="check" /> : current}
            </div>
            <div className="UFOV-brain-games__game-cue">
                {n === 1
                    ? copy.nBackOneCue
                    : formatGameCopy(copy.nBackManyCue, { count: n })}
            </div>
            <div className="UFOV-brain-games__split-actions">
                <GameButton
                    aria-label={copy.noMatch}
                    className={choice === false ? (lastCorrect ? 'is-correct' : 'is-wrong') : ''}
                    disabled={isPaused || done || choice !== null}
                    icon="x"
                    onClick={() => answer(false)}
                    variant="secondary"
                />
                <GameButton
                    aria-label={copy.match}
                    className={choice === true ? (lastCorrect ? 'is-correct' : 'is-wrong') : ''}
                    disabled={isPaused || done || choice !== null}
                    icon="check"
                    onClick={() => answer(true)}
                />
            </div>
            {done ? <GameResultOverlay metrics={metrics} onRestart={reset} /> : null}
        </GameShell>
    );
}

export default SymbolNBackGame;
